import { useMutation } from '@tanstack/react-query';
import { findIndex } from 'lodash';
import { produce } from 'immer';
import { RawDraftContentState } from 'draft-js';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import {
  editCellInTableWidget,
  insertColumnInTableWidget,
  insertRowInTableWidget,
  removeColumnFromTableWidget,
  removeRowFromTableWidget,
} from '../../helpers/api/guidelinesApi';
import { Page, TableWidget } from '../../types';
import { GUIDE_KEYS } from '../../queries/guides';
import {
  editCellsContent,
  insertColumn,
  insertRow,
  removeColumn,
  removeRow,
} from '../../helpers/TableHelper';
import { queryClient } from '../../constants/app';
import { useGuideStore } from '../../stores/guideStore';

enum TableOperation {
  ADD_ROW = 'ADD_ROW',
  ADD_COLUMN = 'ADD_COLUMN',
  REMOVE_ROW = 'REMOVE_ROW',
  REMOVE_COLUMN = 'REMOVE_COLUMN',
}

export const useTableWidgetMutations = (
  sectionId: string,
  columnIndex: number,
  widgetId: string,
) => {
  const activeGuideId = useGuideStore(s => s.activeGuideId) as string;
  const activePageId = useGuideStore(s => s.activePageId) as string;
  const activeChapterId = useGuideStore(s => s.activeChapterId) as string;
  const activeGuideVersion = useGuideStore(s => s.activeGuideVersion) as number;

  return {
    addRow: useMutation(
      (options: { rowIndex: number }) =>
        insertRowInTableWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          columnIndex,
          widgetId,
          options,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateTableLayout(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.rowIndex,
            TableOperation.ADD_ROW,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Edit Table', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    addColumn: useMutation(
      (options: { columnIndex: number }) =>
        insertColumnInTableWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          columnIndex,
          widgetId,
          options,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateTableLayout(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.columnIndex,
            TableOperation.ADD_COLUMN,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Edit Table', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    removeRow: useMutation(
      (options: { rowIndex: number }) =>
        removeRowFromTableWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          columnIndex,
          widgetId,
          options,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateTableLayout(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.rowIndex,
            TableOperation.REMOVE_ROW,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Edit Table', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    removeColumn: useMutation(
      (options: { columnIndex: number }) =>
        removeColumnFromTableWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          columnIndex,
          widgetId,
          options,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateTableLayout(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.columnIndex,
            TableOperation.REMOVE_COLUMN,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Edit Table', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    editCell: useMutation(
      (options: {
        rowIndex: number;
        columnIndex: number;
        content: RawDraftContentState;
      }) =>
        editCellInTableWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          columnIndex,
          widgetId,
          [options],
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateTableContent(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Edit Table', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
  };
};

const optimisticallyUpdateTableLayout = (
  activeGuideId: string,
  activePageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  tableIndex: number,
  operation: TableOperation,
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      if (old) {
        return produce(old, draft => {
          const tableWidget = old.sections[sectionIndex].columns[columnIndex]
            .widgets[widgetIndex] as TableWidget;

          let newTable;

          switch (operation) {
            case TableOperation.ADD_ROW:
              newTable = insertRow(tableWidget.table, tableIndex);
              break;
            case TableOperation.ADD_COLUMN:
              newTable = insertColumn(tableWidget.table, tableIndex);
              break;
            case TableOperation.REMOVE_ROW:
              newTable = removeRow(tableWidget.table, tableIndex);
              break;
            case TableOperation.REMOVE_COLUMN:
              newTable = removeColumn(tableWidget.table, tableIndex);
              break;

            default:
              break;
          }

          tableWidget.table = newTable;

          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = tableWidget;
        });
      }
    },
  );
};

const optimisticallyUpdateTableContent = (
  activeGuideId: string,
  activePageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  variables: {
    rowIndex: number;
    columnIndex: number;
    content: RawDraftContentState;
  },
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      const tableWidget = {
        ...old.sections[sectionIndex].columns[columnIndex].widgets[widgetIndex],
      } as TableWidget;

      if (old) {
        return produce(old, draft => {
          const newTable = editCellsContent(tableWidget.table, [variables]);
          tableWidget.table = newTable;

          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = tableWidget;
        });
      }
    },
  );
};
